import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useFormContext } from 'react-hook-form'
import { InputAdornment } from '@mui/material'

import DeleteIcon from '@mui/icons-material/Delete'

import FormSelect from '../inputFields/FormSelect'
import FormInput from '../inputFields/FormInput'
import AutocompleteSelect from '../inputFields/AutocompleteSelect'
import { SelectMenuItem } from '../styles/TextField'
import { billingTypeOptions, billingUnitOptions } from '../../util/billingConsts'
import { FlexEndContainer } from '../styles/Containers'
import { SecondaryIconButton } from '../common/Button'
import { BoldBodyText } from '../styles/Text'
import { calculateLineItemTotal } from '../../util/strings'
import { preventDecimalInput } from '../../util/inputHandlers'
import { RowContainer, Row, DataFields, Total } from '../styles/BillStyles'

const MIN_TAX_PERCENT = 0
const MAX_TAX_PERCENT = 100

const CenteredRow = styled(Total)`
  min-height: 38px;
`

function BillLineItemForm({ id, onDelete = () => {}, disabled = false, onEdit = () => {} }) {
  const { watch, getValues, setValue } = useFormContext()
  const [defaultType, setDefaultType] = useState()
  const [total, setTotal] = useState('')

  useEffect(() => {
    setDefaultType(getValues(id)?.type || '')
  }, [])

  const type = watch(`${id}.type`)
  const quantity = watch(`${id}.quantity`)
  const price = watch(`${id}.price`)
  const tax = watch(`${id}.tax`)

  useEffect(() => {
    const total = calculateLineItemTotal({ quantity, price, tax })
    const totalString = type === 'DISCOUNT' ? `($ ${total})` : `$ ${total}`
    setTotal(totalString)
    onEdit()
  }, [type, quantity, price, tax])

  useEffect(() => {
    if (tax === '' || tax === undefined || tax === null) return
    const field = `${id}.tax`
    if (tax < MIN_TAX_PERCENT) setValue(field, MIN_TAX_PERCENT)
    else if (tax > MAX_TAX_PERCENT) setValue(field, MAX_TAX_PERCENT)
  }, [tax, id, setValue])

  return (
    <RowContainer>
      <Row editable>
        <DataFields editable>
          {defaultType !== undefined && (
            <FormSelect
              id={`${id}.type`}
              options={billingTypeOptions}
              defaultValue={defaultType}
              basis="15%"
              minwidth="100px"
              disabled={disabled}
              props={{ inputProps: { 'aria-label': 'Charge Type' } }}>
              {billingTypeOptions.map(b => (
                <SelectMenuItem key={b.value} value={b.value}>
                  {b.label}
                </SelectMenuItem>
              ))}
            </FormSelect>
          )}
          <FormInput
            id={`${id}.description`}
            basis="33%"
            minwidth="125px"
            disabled={disabled}
            props={{ inputProps: { 'aria-label': 'Description' } }}
          />
          <FormInput
            id={`${id}.quantity`}
            basis="11%"
            minwidth="75px"
            disabled={disabled}
            props={{ type: 'number', inputProps: { 'aria-label': 'Quantity', step: '0.01' } }}
          />
          <AutocompleteSelect
            id={`${id}.unit`}
            options={billingUnitOptions}
            basis="18%"
            minwidth="120px"
            disabled={disabled}
            storeStringValue
            props={{ inputProps: { 'aria-label': 'Unit' } }}
          />
          <FormInput
            id={`${id}.price`}
            basis="12%"
            minwidth="75px"
            disabled={disabled}
            props={{ type: 'number', inputProps: { 'aria-label': 'Price', step: '0.01' } }}
            inputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
          />
          <FormInput
            id={`${id}.tax`}
            basis="10%"
            minwidth="75px"
            disabled={disabled}
            props={{
              type: 'number',
              inputProps: {
                'aria-label': 'Tax',
                step: '1',
                onKeyPress: preventDecimalInput,
                min: MIN_TAX_PERCENT,
                max: MAX_TAX_PERCENT
              }
            }}
            inputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>
            }}
          />
        </DataFields>
        <CenteredRow>
          <FlexEndContainer>
            <BoldBodyText>{total}</BoldBodyText>
          </FlexEndContainer>
          <SecondaryIconButton onClick={onDelete} aria-label="Delete item" disabled={disabled}>
            <DeleteIcon />
          </SecondaryIconButton>
        </CenteredRow>
      </Row>
    </RowContainer>
  )
}

BillLineItemForm.propTypes = {
  id: PropTypes.string.isRequired,
  onDelete: PropTypes.func,
  disabled: PropTypes.bool,
  onEdit: PropTypes.func
}

export default BillLineItemForm
